import { formatPrice, cn } from '../lib/utils';
import { Badge } from './ui/badge';
import type { CartItem, Mode } from '../lib/types';

const CONDITION_LABELS: Record<string, string> = {
  nm: 'NM',
  lp: 'LP',
  mp: 'MP',
  hp: 'HP',
  dm: 'DMG',
};

const CONDITION_BADGE_CLASS: Record<string, string> = {
  nm: 'badge-nm',
  lp: 'badge-lp',
  mp: 'badge-mp',
  hp: 'badge-hp',
  dm: 'badge-dm',
};

interface ShowTotalOverlayProps {
  items: CartItem[];
  total: number;
  itemCount: number;
  mode: Mode;
  onDismiss: () => void;
}

export function ShowTotalOverlay({
  items,
  total,
  itemCount,
  mode,
  onDismiss,
}: ShowTotalOverlayProps) {
  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-background cursor-pointer"
      onClick={onDismiss}
    >
      {/* Mode label */}
      <div className="flex justify-center pt-8 pb-4">
        <Badge variant={mode === 'buy' ? 'tertiary' : 'default'} className="px-4 py-1.5 text-sm">
          {mode === 'buy' ? 'Buying' : 'Selling'} &mdash; {itemCount} card
          {itemCount !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Itemized list */}
      <div className="flex-1 overflow-auto px-6 py-2">
        <div className="mx-auto max-w-md space-y-1.5">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between rounded-lg bg-muted px-4 py-2.5"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span
                  className={cn(
                    'flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium',
                    CONDITION_BADGE_CLASS[item.condition] || 'badge-nm'
                  )}
                >
                  {CONDITION_LABELS[item.condition] || item.condition.toUpperCase()}
                </span>
                <span className="truncate text-sm text-foreground">
                  {item.cardName}
                </span>
              </div>
              <span
                className={cn(
                  'flex-shrink-0 ml-3 text-sm font-semibold',
                  mode === 'buy' ? 'text-tertiary' : 'text-primary'
                )}
              >
                {formatPrice(item.finalPrice)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Grand total */}
      <div className="border-t border-outline px-6 py-6 text-center">
        <span
          className={cn(
            'text-6xl font-bold',
            mode === 'buy' ? 'text-tertiary' : 'text-primary'
          )}
        >
          {formatPrice(total)}
        </span>
        <p className="mt-3 text-sm text-muted-foreground">
          Tap anywhere to dismiss
        </p>
      </div>
    </div>
  );
}
